<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('textNumber.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('textNumber.title') }}</h1>
      <p>{{ $t('textNumber.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="number-options">
        <div class="option-group">
          <label>{{ $t('textNumber.startNum') }}</label>
          <input type="number" v-model.number="startNum" min="0" />
        </div>
        <div class="option-group">
          <label>{{ $t('textNumber.separator') }}</label>
          <select v-model="separator">
            <option value=". ">1. </option>
            <option value=") ">1) </option>
            <option value="、">1、</option>
            <option value=": ">1: </option>
            <option value="  ">{{ $t('textNumber.tab') }}</option>
          </select>
        </div>
        <div class="option-group">
          <label>{{ $t('textNumber.position') }}</label>
          <select v-model="position">
            <option value="before">{{ $t('textNumber.before') }}</option>
            <option value="after">{{ $t('textNumber.after') }}</option>
          </select>
        </div>
      </div>

      <div class="options">
        <label class="option-label">
          <input type="checkbox" v-model="skipEmpty" />
          {{ $t('textNumber.skipEmpty') }}
        </label>
        <label class="option-label">
          <input type="checkbox" v-model="padZero" />
          {{ $t('textNumber.padZero') }}
        </label>
      </div>

      <div class="actions">
        <button class="btn primary" @click="addNumbers">{{ $t('textNumber.addNumber') }}</button>
        <button class="btn" @click="removeNumbers">{{ $t('textNumber.removeNumber') }}</button>
        <button class="btn" @click="copyResult" :disabled="!output">{{ $t('common.copyResult') }}</button>
        <button class="btn" @click="clearAll">{{ $t('common.clear') }}</button>
      </div>

      <div v-if="successMessage" class="alert success">{{ successMessage }}</div>

      <div class="editor-container">
        <div class="editor-panel">
          <label>{{ $t('textNumber.inputLabel') }}</label>
          <textarea v-model="input" :placeholder="$t('textNumber.inputPlaceholder')" spellcheck="false"></textarea>
        </div>
        <div class="editor-panel">
          <label>{{ $t('textNumber.outputLabel') }}</label>
          <textarea v-model="output" readonly :placeholder="$t('textNumber.outputPlaceholder')" spellcheck="false"></textarea>
        </div>
      </div>
    </div>

    <div class="tool-description">
      <h2>{{ $t('textNumber.whatIs') }}</h2>
      <p>{{ $t('textNumber.whatIsDesc') }}</p>
      <h2>{{ $t('textNumber.useCasesTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('textNumber.useCases')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TextNumber',
  data() {
    return {
      input: '', output: '', successMessage: '',
      startNum: 1, separator: '. ', position: 'before',
      skipEmpty: true, padZero: false
    }
  },
  methods: {
    addNumbers() {
      if (!this.input) return
      const lines = this.input.split('\n')
      let num = this.startNum
      const maxNum = this.startNum + lines.length
      const padLen = String(maxNum).length

      const result = lines.map(line => {
        if (this.skipEmpty && line.trim() === '') return line
        let numStr = String(num)
        if (this.padZero) numStr = numStr.padStart(padLen, '0')
        num++
        if (this.position === 'before') return numStr + this.separator + line
        return line + this.separator + numStr
      })

      this.output = result.join('\n')
      this.successMessage = this.$t('textNumber.success')
      setTimeout(() => { this.successMessage = '' }, 2000)
    },
    removeNumbers() {
      if (!this.input) return
      const lines = this.input.split('\n')
      const result = lines.map(line => {
        return line.replace(/^\s*\d+[.)、:\s]\s*/, '')
      })
      this.output = result.join('\n')
      this.successMessage = this.$t('textNumber.removeSuccess')
      setTimeout(() => { this.successMessage = '' }, 2000)
    },
    async copyResult() {
      if (!this.output) return
      try { await navigator.clipboard.writeText(this.output); this.successMessage = this.$t('common.copied') }
      catch { this.successMessage = this.$t('common.copyFailed') }
      setTimeout(() => { this.successMessage = '' }, 2000)
    },
    clearAll() { this.input = ''; this.output = ''; this.successMessage = '' }
  }
}
</script>

<style scoped>
.tool-page { max-width: 1200px; margin: 0 auto; }
.breadcrumb { margin-bottom: 24px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb a:hover { text-decoration: underline; }
.breadcrumb .separator { margin: 0 8px; color: var(--text-muted); }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 32px; margin-bottom: 12px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 16px; max-width: 600px; margin: 0 auto; line-height: 1.6; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; box-shadow: var(--shadow-md); }
.number-options { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 16px; }
.option-group { display: flex; flex-direction: column; gap: 6px; }
.option-group label { font-size: 14px; color: var(--text-secondary); }
.option-group input, .option-group select { padding: 8px 12px; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; color: var(--text-primary); font-size: 14px; }
.option-group input:focus, .option-group select:focus { outline: none; border-color: var(--accent-color); }
.option-group input[type="number"] { width: 80px; }
.options { display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 16px; }
.option-label { display: flex; align-items: center; gap: 6px; color: var(--text-secondary); font-size: 14px; cursor: pointer; }
.actions { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.btn { background: var(--bg-tertiary); color: var(--text-primary); border: 1px solid var(--border-color); border-radius: 8px; padding: 10px 20px; cursor: pointer; font-size: 14px; transition: all 0.2s; }
.btn:hover { background: var(--bg-hover); }
.btn.primary { background: var(--accent-color); border-color: var(--accent-color); color: white; }
.btn.primary:hover { background: var(--accent-hover); }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.alert.success { padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.3); color: var(--success-color); }
.editor-container { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
@media (max-width: 768px) { .editor-container { grid-template-columns: 1fr; } .number-options { flex-direction: column; } }
.editor-panel { display: flex; flex-direction: column; }
.editor-panel label { font-size: 14px; color: var(--text-secondary); margin-bottom: 8px; }
.editor-panel textarea { min-height: 350px; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 16px; color: var(--text-primary); font-family: 'Monaco','Menlo','Ubuntu Mono',monospace; font-size: 13px; line-height: 1.5; resize: vertical; width: 100%; box-sizing: border-box; }
.editor-panel textarea:focus { outline: none; border-color: var(--accent-color); }
.tool-description { margin-top: 32px; background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; box-shadow: var(--shadow-sm); }
.tool-description h2 { font-size: 20px; color: var(--text-primary); margin: 24px 0 16px; padding-bottom: 8px; border-bottom: 2px solid var(--accent-light); }
.tool-description h2:first-child { margin-top: 0; }
.tool-description p { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 16px; }
.tool-description ul { padding-left: 24px; margin-bottom: 16px; }
.tool-description li { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 8px; }
</style>
